/**
 * Marqueurs des cartes de livraison.
 *
 * Les icônes sont dessinées ici, en SVG encodé dans l'URL. Elles venaient de
 * cdnjs et de raw.githubusercontent : trois requêtes vers deux domaines tiers
 * pour afficher deux punaises, qui échouent hors ligne, derrière un filtrage
 * réseau, ou le jour où le dépôt GitHub d'où sortait l'image disparaît — et
 * une carte sans marqueur ne dit pas qu'il lui manque une image, elle est
 * simplement vide. Ces punaises-ci sont dans le bundle.
 */

import L from 'leaflet';

/** SVG → data-URI. `encodeURIComponent` suffit et reste lisible dans l'inspecteur. */
function svgUrl(svg: string): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/** Punaise classique, colorée, avec un point clair au centre. */
function punaise(couleur: string, bordure: string): string {
  return svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
  <path d="M12.5 0.5C6 0.5 0.5 5.9 0.5 12.6c0 9 10.3 26 11.5 27.6.3.4.7.4 1 0 1.2-1.6 11.5-18.6 11.5-27.6C24.5 5.9 19 .5 12.5.5z" fill="${couleur}" stroke="${bordure}" stroke-width="1"/>
  <circle cx="12.5" cy="12.5" r="4.5" fill="#ffffff"/>
</svg>`);
}

/** Ombre douce, pour que la punaise ne flotte pas au-dessus de la carte. */
const OMBRE_URL = svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="41" height="41" viewBox="0 0 41 41">
  <ellipse cx="14" cy="38" rx="11" ry="3" fill="rgba(0,0,0,0.25)"/>
</svg>`);

const CLIENT_URL = punaise('#16a34a', '#14532d');
const LIVREUR_URL = punaise('#dc2626', '#7f1d1d');

/** Destination du client. */
export const customerIcon = new L.Icon({
  iconUrl: CLIENT_URL,
  shadowUrl: OMBRE_URL,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

/** Destination du client, en plus petit (vue d'ensemble du magasin). */
export const customerIconSmall = new L.Icon({
  iconUrl: CLIENT_URL,
  shadowUrl: OMBRE_URL,
  iconSize: [20, 33],
  iconAnchor: [10, 33],
  popupAnchor: [0, -28],
  shadowSize: [33, 33],
});

/** Position du livreur. `className` porte l'animation de la page de suivi. */
export const driverIcon = new L.Icon({
  iconUrl: LIVREUR_URL,
  shadowUrl: OMBRE_URL,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const driverIconAnimated = new L.Icon({
  iconUrl: LIVREUR_URL,
  shadowUrl: OMBRE_URL,
  iconSize: [32, 52],
  iconAnchor: [16, 52],
  popupAnchor: [1, -44],
  shadowSize: [52, 52],
  className: 'driver-marker-animated',
});

/**
 * Les icônes par défaut de Leaflet (celles d'un `Marker` sans `icon`) pointent
 * sur des fichiers que le bundler ne résout pas. On les remplace ici, une fois,
 * pour que le cas soit couvert même si un marqueur est ajouté sans icône.
 */
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: CLIENT_URL,
  iconUrl: CLIENT_URL,
  shadowUrl: OMBRE_URL,
});

/** Échappement minimal pour un texte placé dans le HTML d'un marqueur. */
const html = (t: string) =>
  String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/**
 * Arrêt d'une tournée, numéroté dans l'ordre de passage (23/10/2026) : couleur de
 * la tournée ; livré = gris coché, échec = rouge barré ; le PROCHAIN est plus
 * grand et cerclé. Styles en ligne : ce HTML vit hors de React et de Tailwind.
 */
export function iconeArret(
  rang: number,
  couleur: string,
  etat: 'a_faire' | 'en_route' | 'livre' | 'echec',
  prochain: boolean,
): L.DivIcon {
  const taille = prochain ? 34 : 26;
  const fond = etat === 'livre' ? '#9ca3af' : etat === 'echec' ? '#dc2626' : couleur;
  const texte = etat === 'livre' ? '✓' : etat === 'echec' ? '✗' : String(rang);
  const ombre = prochain ? `0 0 0 5px ${couleur}55, 0 1px 4px rgba(0,0,0,.35)` : '0 1px 3px rgba(0,0,0,.3)';
  return L.divIcon({
    className: '',
    html:
      `<div style="width:${taille}px;height:${taille}px;border-radius:9999px;background:${fond};color:#fff;` +
      `font:700 ${prochain ? 15 : 13}px/1 system-ui,-apple-system,sans-serif;display:flex;align-items:center;` +
      `justify-content:center;border:2px solid #fff;box-shadow:${ombre};">${texte}</div>`,
    iconSize: [taille, taille],
    iconAnchor: [taille / 2, taille / 2],
    popupAnchor: [0, -taille / 2],
  });
}

/** Le livreur : une étiquette à son prénom, couleur de sa tournée ; pâlie si la position est ancienne. */
export function iconeLivreur(nom: string, couleur: string, ancienne: boolean): L.DivIcon {
  const prenom = html((nom || 'Livreur').trim().split(/\s+/)[0]);
  return L.divIcon({
    className: '',
    html:
      `<div style="transform:translate(-50%,-100%);display:inline-flex;align-items:center;gap:4px;white-space:nowrap;` +
      `padding:4px 8px;border-radius:9999px;background:#fff;border:3px solid ${couleur};color:#111827;` +
      `font:700 13px/1.1 system-ui,-apple-system,sans-serif;box-shadow:0 2px 6px rgba(0,0,0,.35);` +
      `opacity:${ancienne ? 0.6 : 1};">🚚 ${prenom}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    popupAnchor: [0, -34],
  });
}

/** Le magasin, point de départ des tournées. */
export const iconeDepot: L.DivIcon = L.divIcon({
  className: '',
  html:
    '<div style="transform:translate(-50%,-50%);width:30px;height:30px;border-radius:8px;background:#fff;' +
    'border:2px solid #374151;display:flex;align-items:center;justify-content:center;font-size:16px;' +
    'box-shadow:0 1px 3px rgba(0,0,0,.3);">🏠</div>',
  iconSize: [0, 0],
  iconAnchor: [0, 0],
  popupAnchor: [0, -16],
});
